import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { VAR_REFRESH_TOKEN } from '../../../../../shared/config/consts';
import { ToastService } from '../../components/common';
import { AuthService, UserService } from '../../core/services';
import { AuthActions } from './auth.actions';

// Define the JwtPayload interface
interface JwtPayload {
  roles: string[];
  permissions: string[];
  exp: number;
  iat: number;
  sub: string;
}

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);
  private userService = inject(UserService);
  private toast = inject(ToastService);

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ email, password }) =>
        this.authService.login(email, password).pipe(
          map((response) => {
            return AuthActions.loginSuccess({
              message: response.message,
              token: response?.payload?.accessToken,
              refreshToken: response?.payload?.refreshToken ?? '',
            });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.loginFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  loginAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginAdmin),
      mergeMap(({ email, password }) =>
        this.authService.loginAdmin(email, password).pipe(
          map((response) => {
            return AuthActions.loginAdminSuccess({
              message: response.message,
              token: response.payload.accessToken,
              refreshToken: response.payload.refreshToken ?? '',
              requirePasswordChange: response.payload.requirePasswordChange,
            });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.loginAdminFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  loginAdminSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginAdminSuccess),
        tap(({ token, refreshToken, requirePasswordChange }) => {
          localStorage.setItem('accessToken', token);
          localStorage.setItem(VAR_REFRESH_TOKEN, refreshToken);

          // Check if password change is required
          if (requirePasswordChange) {
            this.router.navigate(['auth/reset-password']);
            return;
          }

          const decodedToken = jwtDecode<JwtPayload>(token);

          const userRoles = decodedToken.roles;

          if (userRoles.includes('SUPER_ADMIN')) {
            localStorage.setItem('userType', 'SUPER_ADMIN');
            this.router.navigate(['/dashboard/complaints']);
          } else {
            localStorage.setItem('userType', 'ADMIN');
            this.router.navigate(['/dashboard/cases']);
          }

          if (userRoles.includes('ADMIN')) {
            this.store.dispatch(AuthActions.getCurrentUser());
          } else {
            // TODO: Handle admin login failure
          }
        })
      );
    },
    { dispatch: false }
  );

  loginAdminFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginAdminFailure),
        tap(({ error }) => {
          this.toast.show(error, 'error');
        })
      );
    },
    { dispatch: false }
  );

  loginMinalocUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginMinalocUsers),
      mergeMap(({ code }) =>
        this.authService.loginMinalocUsers(code).pipe(
          map((response) => {
            return AuthActions.loginMinalocUsersSuccess({
              message: response.message,
              token: response.payload.accessToken,
              refreshToken: response.payload.refreshToken ?? '',
            });
          }),
          catchError(() => {
            return of(
              AuthActions.loginMinalocUsersFailure({
                error: 'Login Failed. Refresh the page and try again.',
              })
            );
          })
        )
      )
    );
  });

  loginMinalocUsersSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginMinalocUsersSuccess),
        tap(({ token }) => {
          localStorage.setItem('accessToken', token);
          localStorage.setItem('userType', 'ADMIN');
          this.store.dispatch(AuthActions.getCurrentUser());
          this.router.navigate(['/dashboard/cases']);
        })
      );
    },
    { dispatch: false }
  );

  loginMinalocUsersFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginMinalocUsersFailure),
        tap(({ error }) => {
          this.toast.show(error, 'error');
        })
      );
    },
    { dispatch: false }
  );

  loginSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, refreshToken }) => {
          localStorage.setItem('accessToken', token);
          localStorage.setItem(VAR_REFRESH_TOKEN, refreshToken);
          localStorage.setItem('userType', 'CITIZEN');
          const decodedToken = jwtDecode<JwtPayload>(token);
          const userRoles = decodedToken.roles;

          if (userRoles.includes('CITIZEN')) {
            this.store.dispatch(AuthActions.getCurrentUser());
            this.router.navigate(['/citizen']);
          } else {
            // TODO: Handle citizen login failure
          }
        })
      );
    },
    { dispatch: false }
  );

  register$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.register),
      mergeMap(({ requestBody }) =>
        this.authService.register(requestBody).pipe(
          map((response) =>
            AuthActions.registerSuccess({
              message: response.message,
            })
          ),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.registerFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  registerSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.registerSuccess),
        tap(() => this.router.navigate(['/auth']))
      );
    },
    { dispatch: false }
  );

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() =>
        this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError(() => {
            return of(AuthActions.logoutSuccess());
          })
        )
      )
    );
  });

  logoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          if (
            localStorage.getItem('userType') === 'SUPER_ADMIN' ||
            localStorage.getItem('userType') === 'ADMIN'
          ) {
            this.router.navigate(['/auth']);
          } else {
            this.router.navigate(['/citizen/auth']);
          }

          localStorage.clear();
        })
      );
    },
    { dispatch: false }
  );

  getCurrentUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getCurrentUser),
      mergeMap(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          return of(
            AuthActions.getCurrentUserFailure({ error: 'No token found' })
          );
        }

        // check if the token is expired
        const decodedToken = jwtDecode<JwtPayload>(token);
        if (decodedToken.exp && decodedToken.exp < Date.now() / 1000) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('userType');
          localStorage.removeItem('organizationId');
          return of(
            AuthActions.getCurrentUserFailure({
              error: 'Session expired. Please login again.',
            })
          );
        }

        return this.userService.getMe().pipe(
          map((response) => {
            const organizationId = response.payload.organizationId;
            if (organizationId) {
              localStorage.setItem('organizationId', organizationId);
            }
            const names = response.payload.fullName.split(' ');
            const user = {
              email: response.payload.email,
              firstName: names[0] || '',
              lastName: names[1] || '',
              fullName: response.payload.fullName,
              phoneNumber: response.payload.phoneNumber,
              nationalIdNumber: response.payload.nationalIdNumber,
              gender: response.payload.gender,
              permissions: response.payload.permissions,
              organizationId: response.payload.organizationId,
              organizationName: response.payload.organizationName,
              profilePictureUrl: response.payload.profilePictureUrl,
              occupation: response.payload.occupation,
              trackingLevel: response.payload.trackingLevel,
              assignedLocations: response.payload.assignedLocations,
            };
            return AuthActions.getCurrentUserSuccess({ user });
          }),
          catchError((error) => {
            // If token is invalid, clear it
            localStorage.removeItem('accessToken');
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              AuthActions.getCurrentUserFailure({ error: errorMessage })
            );
          })
        );
      })
    );
  });

  resetPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.changePassword),
      mergeMap(({ oldPassword, newPassword }) =>
        this.userService.changePassword({ oldPassword, newPassword }).pipe(
          map((response) => {
            return AuthActions.changePasswordSuccess({
              message: response.message,
            });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              AuthActions.changePasswordFailure({ error: errorMessage })
            );
          })
        )
      )
    );
  });

  changePasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.changePasswordSuccess),
        tap(() => {
          localStorage.removeItem('accessToken');
          this.router.navigate(['/auth']);
          this.toast.show('Password changed successfully', 'success');
        })
      );
    },
    { dispatch: false }
  );

  resetPasswordFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.changePasswordFailure),
        tap(({ error }) => {
          this.toast.show(error, 'error');
        })
      );
    },
    { dispatch: false }
  );
}
